// Needs Attention — the reusable list of work items whose health is not
// "on track", with a health chip + "Why?" per row. Used by My workload, the
// person drill-down, and under Team (server-filtered by health). Titles link
// to /app/work/{id}; when the caller may not see titles (`hideTitles`) the
// list shows counts only — never a title the server withheld.
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import type { AttentionItem } from '@/services/workloadService';
import type { WorkHealth } from '@/services/workService';
import { HEALTH_ORDER, HealthChip, healthLabel } from './health';
import { fmtDT } from '../../workassignments/shared';

/** Filter chips — every non-on-track health, in severity order. */
export const ATTENTION_FILTERS: WorkHealth[] = ['OVERDUE', 'AT_RISK', 'STALE', 'BLOCKED', 'AWAITING_REVIEW'];

export interface NeedsAttentionProps {
  items: AttentionItem[];
  loading?: boolean;
  /** Show the health filter chips. Uncontrolled = client-side filter over
   * `items`; pass `health` + `onHealth` to let the parent refetch server-side. */
  filterable?: boolean;
  health?: WorkHealth | '';
  onHealth?: (h: WorkHealth | '') => void;
  /** Counts only — the caller's scope does not include item titles. */
  hideTitles?: boolean;
  /** Show the owner name on each row (team lists). */
  showOwner?: boolean;
  title?: string;
  emptyLabel?: string;
  /** Total when the parent pages server-side (falls back to items.length). */
  total?: number;
  className?: string;
}

export const NeedsAttention: React.FC<NeedsAttentionProps> = ({
  items, loading, filterable, health, onHealth, hideTitles, showOwner, title, emptyLabel, total, className = '',
}) => {
  const { t } = useTranslation('work');
  const tt = t as unknown as (key: string, opts?: Record<string, unknown>) => string;
  const [localHealth, setLocalHealth] = useState<WorkHealth | ''>('');
  const controlled = typeof onHealth === 'function';
  const active = controlled ? (health || '') : localHealth;
  const setActive = (h: WorkHealth | '') => (controlled ? onHealth!(h) : setLocalHealth(h));

  const visible = useMemo(() => {
    const sorted = [...items].sort((a, b) => HEALTH_ORDER.indexOf(a.primary_health) - HEALTH_ORDER.indexOf(b.primary_health));
    return controlled || !active ? sorted : sorted.filter((i) => i.primary_health === active);
  }, [items, active, controlled]);
  const count = total ?? visible.length;

  return (
    <section className={`space-y-2 ${className}`} data-testid="needs-attention">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-h4 text-charcoal">
          {title || t('needs_attention', { defaultValue: 'Needs attention' })}
          {count > 0 && <span className="text-muted-foreground"> · {count}</span>}
        </h2>
        {filterable && (
          <div className="flex flex-wrap gap-1" role="group" aria-label={t('health_filter', { defaultValue: 'Filter by health' }) as string}>
            <button type="button" aria-pressed={active === ''} onClick={() => setActive('')}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${active === '' ? 'bg-primary-500 text-white border-primary-500' : 'bg-background text-muted-foreground border-border hover:bg-muted'}`}>
              {t('common.all', { defaultValue: 'All' })}
            </button>
            {ATTENTION_FILTERS.map((h) => (
              <button key={h} type="button" aria-pressed={active === h} onClick={() => setActive(active === h ? '' : h)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${active === h ? 'bg-primary-500 text-white border-primary-500' : 'bg-background text-muted-foreground border-border hover:bg-muted'}`}>
                {healthLabel(tt, h)}
              </button>
            ))}
          </div>
        )}
      </div>
      <Card><CardContent className="p-0">
        {loading && items.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground flex items-center gap-2" role="status">
            <Loader2 className="w-4 h-4 animate-spin" /> {t('common.loading', { defaultValue: 'Loading…' })}
          </p>
        ) : visible.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground" data-testid="needs-attention-empty">
            {active
              ? t('attention_no_match', { defaultValue: 'No work items with this health right now.' })
              : emptyLabel || t('attention_none', { defaultValue: 'Nothing needs attention right now.' })}
          </p>
        ) : hideTitles ? (
          <p className="p-6 text-sm text-muted-foreground" data-testid="needs-attention-counts-only">
            {t('attention_counts_only', { defaultValue: '{{count}} work item(s) need attention. Item details are outside your visibility scope.', count: visible.length })}
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {visible.map((w) => (
              <li key={w.id} className="flex items-center justify-between gap-3 py-3 px-4" data-testid={`attention-row-${w.id}`}>
                <div className="min-w-0">
                  <Link to={`/app/work/${w.id}`} className="text-foreground hover:underline truncate block">
                    {w.title}
                    {!!w.parent_id && <span className="ml-2 text-xs text-muted-foreground">{t('subitem_tag', { defaultValue: 'sub-item' })}</span>}
                  </Link>
                  <p className="text-xs text-muted-foreground tabular-nums">
                    {showOwner && w.owner_name ? `${w.owner_name} · ` : ''}
                    {w.planned_end_at
                      ? `${t('due', { defaultValue: 'Due' })} ${fmtDT(w.planned_end_at)}`
                      : w.last_meaningful_activity_at
                        ? `${t('last_activity', { defaultValue: 'Last activity' })} ${fmtDT(w.last_meaningful_activity_at)}`
                        : ''}
                    {w.explain?.[0] ? ` · ${w.explain[0]}` : ''}
                  </p>
                </div>
                <HealthChip health={w.primary_health} explain={w.explain} signals={w.signals} />
              </li>
            ))}
          </ul>
        )}
      </CardContent></Card>
    </section>
  );
};

export default NeedsAttention;
